import { getNormalizedScrollLeft } from 'normalize-scroll-left';
import BaseBehavior from '../../../framework/base/base';
import { debounce } from '../../../shared/utilities/debounce';
import { onEffectEndEvent } from '../../../shared/utilities/effectsEndEventHandler';
import { isDevice, deviceDetection } from '../../../shared/utilities/deviceDetection';
import { whenInView } from '../../../shared/utilities/when-in-view';
import { hideElement, showElement } from '../../../shared/utilities/show-hide';

const contentShadowClasses = {
	scrollShadowLeft: 'v2-content-scroll-shadow--left',
	scrollShadowRight: 'v2-content-scroll-shadow--right',
	scrollShadowBoth: 'v2-content-scroll-shadow--both'
};

const scrollShadowClasses = {
	arrow: '.v2-content-scroll-shadow-arrow',
	content: '.v2-content-scroll-shadow__content',
	arrowAnimate: 'v2-content-scroll-shadow-arrow--animate',
	arrowPlaceholder: '.v2-content-scroll-shadow-arrow__ph'
};

const removeScrollClasses = el => {
	Object.keys(contentShadowClasses).forEach(className => {
		el.classList.remove(contentShadowClasses[className]);
	});
};

let isRtl = false;
export default class ContentScrollShadow extends BaseBehavior {
	constructor(element) {
		super(element);

		this.initDomCache();
		this.horizontalScrollInitialized = false;
		this.shadowContent = element.querySelector(
			element.hasAttribute('data-element') ? element.getAttribute('data-element') : 'table'
		);
		isRtl = window.getComputedStyle(element).direction === 'rtl';
		this.contentPlaceholder = element.querySelector(scrollShadowClasses.content);
		this.arrow = element.querySelector(scrollShadowClasses.arrow);
		this.arrowPlaceholder = element.querySelector(scrollShadowClasses.arrowPlaceholder);
		if (isDevice(deviceDetection.mobile)) {
			this.initScrollShadow();
		}
	}

	addEventListeners() {
		const handleHorizontalScrollDebounced = debounce(() => {
			this.handleHorizontalScroll();
		}, 5).bind(this);

		this.lastRecordedWindowWidth = window.innerWidth;
		const handleResizeDebounced = debounce(() => {
			if (this.lastRecordedWindowWidth === window.innerWidth) return;
			this.lastRecordedWindowWidth = window.innerWidth;
			if (this.shadowContent.clientWidth > this.contentPlaceholder.clientWidth) {
				showElement(this.arrowPlaceholder);
				this.handleHorizontalScroll();
			} else {
				removeScrollClasses(this.contentPlaceholder);
				hideElement(this.arrowPlaceholder);
			}
		}, 250).bind(this);

		this.addEventListener(this.contentPlaceholder, 'scroll', handleHorizontalScrollDebounced);
		window.addEventListener('resize', handleResizeDebounced);
	}

	initScrollShadow() {
		whenInView(this.contentPlaceholder).then(() => {
			if (this.horizontalScrollInitialized) return;
			debounce(() => {
				if (this.shadowContent.clientWidth > this.contentPlaceholder.clientWidth) {
					showElement(this.arrowPlaceholder);
					this.handleHorizontalScroll();
					this.addEventListeners();
				}
			}).bind(this)();
			this.horizontalScrollInitialized = true;
		});
	}

	removeEventListeners() {
		this.removeEventListener(this.contentPlaceholder, 'scroll');
		this.removeEventListener(window, 'resize');
	}

	animateArrow() {
		this.arrow.classList.add(scrollShadowClasses.arrowAnimate);
		onEffectEndEvent(
			this.arrow,
			() => {
				this.arrow.classList.remove(scrollShadowClasses.arrowAnimate);
			},
			'animation'
		);
	}

	handleHorizontalScroll() {
		const { clientWidth } = this.contentPlaceholder;
		const scrollLeft = isRtl
			? getNormalizedScrollLeft(this.contentPlaceholder, 'rtl')
			: getNormalizedScrollLeft(this.contentPlaceholder, 'ltr');
		if (scrollLeft > 0 && scrollLeft < this.shadowContent.clientWidth - clientWidth) {
			removeScrollClasses(this.contentPlaceholder);
			this.contentPlaceholder.classList.add(contentShadowClasses.scrollShadowBoth);
			return;
		}
		if (scrollLeft === 0) {
			if (!isRtl) this.animateArrow();
			removeScrollClasses(this.contentPlaceholder);
			isRtl
				? this.contentPlaceholder.classList.add(contentShadowClasses.scrollShadowLeft)
				: this.contentPlaceholder.classList.add(contentShadowClasses.scrollShadowRight);
			return;
		}
		if (scrollLeft >= this.shadowContent.clientWidth - clientWidth) {
			removeScrollClasses(this.contentPlaceholder);
			if (isRtl) this.animateArrow();
			isRtl
				? this.contentPlaceholder.classList.add(contentShadowClasses.scrollShadowRight)
				: this.contentPlaceholder.classList.add(contentShadowClasses.scrollShadowLeft);
		}
	}
}
